import { PAYEX_URL } from 'constants/index';
import { decamelizeKeys } from 'humps';
import type { Payment } from 'models/payment';
import { fetch } from 'services/apiV3/fetch';
import type {
  DeleteFileRequestParams,
  DeleteFileResponse,
  FetchDocumentsCollections,
  FetchFileUploadURLRequestParams,
  GenerateFilenameResponse,
  GenerateFilenameUrlRequestParams,
  RepeatPaymentPayload,
  SMSNotificationResponse,
  UpdateSMSNotificationRequestParams,
  UploadFileToUrlRequestParams,
} from './types';

export type PaymentsService = {
  repeatPayment: (payload: RepeatPaymentPayload) => Promise<Payment>;
  updateSMSNotification: (
    payload: UpdateSMSNotificationRequestParams,
  ) => Promise<SMSNotificationResponse>;
  deleteFile: (payload: DeleteFileRequestParams) => Promise<DeleteFileResponse>;
  generateDocumentUrl: (
    payload: GenerateFilenameUrlRequestParams,
  ) => Promise<GenerateFilenameResponse>;
  fetchFileUploadURL: (
    payload: FetchFileUploadURLRequestParams,
  ) => Promise<{ url?: string }>;
  uploadFileToUrl: (payload: UploadFileToUrlRequestParams) => Promise<void>;
  fetchDocumentsCollections: (
    payload: FetchDocumentsCollections,
  ) => Promise<Record<string, unknown>>;
};

const payments: PaymentsService = {
  repeatPayment: ({ id, paymentToken, items, fingerprint, locale }) => {
    const path = `${PAYEX_URL}/${id}/repeat_payment`;
    const payload = { paymentToken, items, fingerprint, locale };
    const body = JSON.stringify(decamelizeKeys(payload));
    const options = { method: 'POST', body };

    return fetch(path, options);
  },

  updateSMSNotification: ({ number, active, url }) => {
    let options;

    switch (active) {
      case true:
        const payload = { number };
        const body = JSON.stringify(payload);
        options = { method: 'POST', body };
        break;
      case false:
        options = { method: 'DELETE' };
        break;
    }

    return fetch(url, options);
  },

  deleteFile: ({ url }) => {
    const path = url;
    const options = { method: 'DELETE' };

    return fetch(path, options);
  },

  generateDocumentUrl: ({ id, token, filename }) => {
    const path = `${PAYEX_URL}/payments/${id}/documents/upload_url?token=${token}`;
    const payload = { filename };
    const body = JSON.stringify(payload);
    const options = { method: 'POST', body };

    return fetch(path, options);
  },

  fetchFileUploadURL: ({ documentUploadURL }) => {
    const options = { method: 'POST' };

    return fetch(documentUploadURL, options);
  },

  uploadFileToUrl: ({ file, url, info }) => {
    const body = new FormData();
    body.append('file', file);
    body.append('info', JSON.stringify(info));

    const path = url;
    const options = { method: 'POST', body };

    return fetch(path, options);
  },

  fetchDocumentsCollections: ({ id, token, locale }) => {
    const url = `${PAYEX_URL}/payments/${id}/documents_collections?token=${token}&locale=${locale}`;
    const options = { method: 'GET' };

    return fetch(url, options);
  },
};

export { payments };
